import axios from 'axios'
import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import {
  ListHandoverHistoryParams,
  HandoverInviteStatus,
  ListPendingApprovalsParams,
  ListApprovalHistoryParams
} from '../Types/Requests/HandoverInviteRequests'
import {
  HandoverInviteResponse,
  PaginatedResponse,
  PaginatedAdminQueueResponse,
  AdminQueueResponse,
  ApprovePreviewResponse
} from '../Types/Responses/HandoverInviteResponses'
import { getQueryString } from '@/utils/string'

interface ExportAcceptedHandoversParams {
  query?: string
  startDate?: string
  endDate?: string
  startTime?: string
  endTime?: string
  status?: string[]
  limit?: number
  offset?: number
  filename?: string
  operationalGroups?: string[]
}

export default class HandoverInviteService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/company/handover-invites'
  }

  async listHistory(
    params: ListHandoverHistoryParams,
    companyIds?: string[]
  ): Promise<PaginatedResponse<HandoverInviteResponse>> {
    const requestOptions: { headers?: Record<string, any> } = {}

    if (companyIds && companyIds.length > 0) {
      requestOptions.headers = {
        company_ids: companyIds.join(',')
      }
    }
    try {
      const { data } = await request.get<PaginatedResponse<HandoverInviteResponse>>(
        `${this.getCompletePath()}/history`,
        params,
        requestOptions
      )

      return data
    } catch (e) {
      throw this.handleError(e, 'Ocorreu um erro ao carregar o histórico de trocas de plantão.')
    }
  }

  async exportAcceptedHandoversXlsx(
    params: ExportAcceptedHandoversParams
  ): Promise<HTMLAnchorElement> {
    const qs = getQueryString(params)
    let data: BlobPart
    try {
      const response = await request.get<BlobPart>(
        `${this.getCompletePath()}/export-accepted-handovers?${qs.toString()}`,
        undefined,
        undefined,
        'blob'
      )
      data = response.data
    } catch (e) {
      // responseType 'blob': o body JSON do erro chega dentro de um Blob. O status segue
      // no `originalError` — a tela detecta o 404 por `getApiErrorStatus`.
      throw this.handleError(
        await unwrapBlobError(e),
        'Erro ao exportar relatório de trocas, contate um administrador!'
      )
    }

    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.download = params.filename ?? 'historico-trocas.xlsx'
    link.style.display = 'none'
    document.body.appendChild(link)
    return link
  }

  // === Handover V2 Methods ===

  async getAdminQueueItem(packageId: number): Promise<AdminQueueResponse> {
    try {
      const { data } = await request.get<AdminQueueResponse>(
        `${this.getCompletePath()}/pending-approvals/${packageId}`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Ocorreu um erro ao buscar detalhes da aprovação.')
    }
  }

  async listPendingApprovals(
    params: ListPendingApprovalsParams
  ): Promise<PaginatedAdminQueueResponse> {
    try {
      const { data } = await request.get<PaginatedAdminQueueResponse>(
        `${this.getCompletePath()}/pending-approvals`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Ocorreu um erro ao buscar aprovações pendentes.')
    }
  }

  async listApprovalHistory(
    params: ListApprovalHistoryParams
  ): Promise<PaginatedAdminQueueResponse> {
    try {
      const { data } = await request.get<PaginatedAdminQueueResponse>(
        `${this.getCompletePath()}/approval-history`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Ocorreu um erro ao buscar histórico de aprovações.')
    }
  }

  async getApprovePreview(packageId: number): Promise<ApprovePreviewResponse> {
    try {
      const { data } = await request.get<ApprovePreviewResponse>(
        `${this.getCompletePath()}/packages/${packageId}/approve-preview`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Ocorreu um erro ao verificar conflitos da aprovação.')
    }
  }

  async adminApprove(packageId: number, cancellationReason?: string): Promise<void> {
    try {
      await request.patch(`${this.getCompletePath()}/packages/${packageId}/approve`, {
        cancellationReason
      })
    } catch (e) {
      // Recusas de negócio (400) chegam com corpo rico — ex.: o bloqueio de horas
      // consecutivas, com `code`, mensagem pronta e as violações. Relançar o erro cru
      // mantém esse corpo disponível para a store; sem response (rede, timeout, erro
      // de código), handleError normaliza num ServiceError com a mensagem da ação.
      // eslint-disable-next-line no-restricted-properties -- relança cru o 400 de horas consecutivas (corpo rico para a store)
      if (axios.isAxiosError(e) && e.response) throw e
      throw this.handleError(e, 'Ocorreu um erro ao aprovar a troca.')
    }
  }

  async adminDecline(packageId: number): Promise<{ hasPendingCandidates: boolean }> {
    try {
      const { data } = await request.patch(
        `${this.getCompletePath()}/packages/${packageId}/decline`,
        {}
      )
      return data as { hasPendingCandidates: boolean }
    } catch (e) {
      throw this.handleError(e, 'Ocorreu um erro ao recusar a troca.')
    }
  }
}

export { HandoverInviteStatus }
